import { ErrorCodes } from '../utils/ErrorCodes';
import Logger from '../utils/Logger';
import Session from '../utils/Session';
import Util from '../utils/Util';
import NawsCommunication from './NawsCommunication';
import NawsEngine from './NawsEngine';

export default class PluginInterface {
	static activeDevice = null;

	static deviceEngine = null;

	static deviceList = [];

	static hasPrinters = false;

	static printerList = [];

	static requiredVersion = { MAC: '300319', WIN: '300320' };

	static showDeviceSelection = false;

	// status can be login, available, not available, upgrade
	static status = 'login';

	/**
	 * Initializes Plugin Instance and get Sessions from NAWS API.
	 */
	static init = () => {
		if (Session.isSignedIn) {
			// get NAWS sessions
			this.getSessions();
		}
	};

	/**
	 * Reset session details before getSessions call is made
	 */
	static resetSessionDetails = () => {
		this.activeDevice = null;
		this.deviceList = [];
		this.hasPrinters = false;
		this.printerList = [];
		this.showDeviceSelection = false;
		this.status = '';
	};

	/**
	 * Set the active device
	 * @param {*} device
	 */
	static setActiveDevice = device => {
		this.activeDevice = device;
	};

	/**
	 * Delete Naws cookie if it exists
	 */
	static deleteStampsConnectCookie = () => {
		const stampsConnectCookie = Util.getCookie('StampsConnectDevice');
		if (stampsConnectCookie) {
			Util.setCookie(
				'StampsConnectDevice',
				null,
				new Date('January 1, 1970'),
				'/'
			);
		}
	};

	/**
	 * Get Sessions from NAWS V3 API.
	 * @param {function} printCallback
	 */
	static getSessions = async printCallback => {
		const getSessionsCallback = resp => {
			const respData = resp.data;

			if (respData.nawsSessionInfo || respData.hasConnected) {
				this.deviceEngine = NawsEngine;
				this.processDevices(respData.nawsSessionInfo, printCallback);
			} else if (!respData.nawsSessionInfo) {
				if (
					respData.hasConnected === false &&
					Util.getCookie('StampsConnectDevice') !== null
				) {
					// This case is for users who have access to NAWS v3 but have not upgraded their plugin to v3
					// hasConnected is false if the user has no data in the NAWS v3 database
					this.status = 'upgrade';

					if (printCallback) {
						printCallback();
					}
				} else {
					this.status = 'notAvailable';

					if (printCallback) {
						printCallback();
					}
				}
			}

			// delete Naws cookie if it exists
			this.deleteStampsConnectCookie();

			if (this.deviceEngine) {
				Logger.logToServer(
					{
						category: 'plugin_init',
						plugin_loaded: this.deviceEngine.className,
					},
					Logger.INFO
				);
			}
		};

		// clear session details before making a call to get sessions
		this.resetSessionDetails();

		const response = await NawsCommunication.getSessions();

		if (NawsCommunication.isSuccessful(response)) {
			getSessionsCallback(response);
		} else {
			this.status = 'notAvailable';

			// delete Naws cookie if it exists
			this.deleteStampsConnectCookie();
		}
	};

	/**
	 * Submits Print Job to Plugin.
	 * @param {object} printObject
	 * @param {boolean} isReprint
	 * @param {function} callback to be executed after print from plugin
	 */
	static print = (printObject, isReprint, callback) => {
		if (isReprint && printObject.reprintCommand) {
			printObject.printCommand = printObject.reprintCommand;
			printObject.printCommandX = printObject.reprintCommandX;
		}

		if (this.status === 'available') {
			this.deviceEngine.print(
				printObject,
				function (responseObject) {
					callback(responseObject);
				},
				this.activeDevice
			);
		}
	};

	/**
	 * Converts semantic version to int
	 * @param {string} pluginVersion
	 * @return {int}
	 */
	static getPluginVersionInt = pluginVersion => {
		let pluginVersionReturn = -1,
			tmp;

		if (!pluginVersion || pluginVersion === '') {
			return pluginVersionReturn;
		} else {
			// remove decimals
			tmp = pluginVersion.replace(/\./g, '');
			tmp = parseInt(tmp, 10);
			pluginVersionReturn = Number.isInteger(tmp) ? tmp : -1;

			return pluginVersionReturn;
		}
	};

	/**
	 * Process Device Details and set plugin status and PreferredNawsSession cookie.
	 * @param {array} deviceArray
	 * @param {function} printCallback
	 */
	static processDevices = (deviceArray, printCallback) => {
		const preferredNawsSession = Util.getCookie('PreferredNawsSession');
		const deviceIndex = 0;
		let nawsDeviceData,
			printerNamesArray = [],
			filteredPrinters = [];

		// devices array is not returned when the connect is in logged out/exited state
		if (deviceArray) {
			// filter out offline devices
			nawsDeviceData = deviceArray
				.filter(
					device =>
						device.SessionID === 'addComputer' ||
						device.NawsSessionStatusId === 1
				)
				.map(device => ({
					SessionID: device.SessionID,
					DateCreated: device.DateCreated,
					DeviceName: device.DeviceName,
					CustId: device.CustId,
					UserId: device.UserId,
					ConversationToken: device.ConversationToken,
					NawsSessionStatusId: device.NawsSessionStatusId,
					LastCommandUpdateTime: device.LastCommandUpdateTime,
					SdcConnectServerHost: device.SdcConnectServerHost,
					SdcConnectVersion: (() => {
						const connectVersionNumber = device.SdcConnectVersion.split(' ');
						const versionNumber = Array.isArray(connectVersionNumber)
							? connectVersionNumber[0]
							: '';

						return versionNumber;
					})(),
					PrinterInfo: (() => {
						const emptyPrinterInfo = {
								printer: [],
							},
							printerInfo =
								device.PrinterInfo && Array.isArray(device.PrinterInfo.printer)
									? device.PrinterInfo
									: emptyPrinterInfo;
						return printerInfo;
					})(),
					PrinterInfoTime: device.PrinterInfoTime,
					SecurityAsset: device.SecurityAsset,
					isUpgradeRequired: (() => {
						const sdcConnectVersion = PluginInterface.getPluginVersionInt(
							device.SdcConnectVersion
						);

						// compare Plugin version to check if the user has to upgrade plugin
						if (
							navigator.platform.toLowerCase().indexOf('win') !== -1 &&
							sdcConnectVersion < PluginInterface.requiredVersion.WINDOWS
						) {
							return true;
						} else if (
							navigator.platform.toLowerCase().indexOf('mac') !== -1 &&
							sdcConnectVersion < PluginInterface.requiredVersion.MAC
						) {
							return true;
						} else {
							return false;
						}
					})(),
				}));
		}

		if (!nawsDeviceData) {
			this.status = 'login';
		} else {
			nawsDeviceData.forEach(deviceDetails => {
				// save active devices in device list array
				this.deviceList.push(deviceDetails);
				if (
					preferredNawsSession &&
					deviceDetails.SessionID === preferredNawsSession
				) {
					this.activeDevice = deviceDetails;
					this.status = 'available';
					this.isNawsConnected = true;
				}

				filteredPrinters = this.filterPrinters(
					deviceDetails.PrinterInfo.printer
				);
				deviceDetails.PrinterInfo.printer = filteredPrinters;

				if (!this.hasPrinters && filteredPrinters.length > 0) {
					this.hasPrinters = true;
				}
			});

			// set first available device as active device when preferred naws session is not available
			if (this.activeDevice === null && this.deviceList.length > 0) {
				this.activeDevice = this.deviceList[deviceIndex];
				Util.setCookie('PreferredNawsSession', this.activeDevice.SessionID);
				this.status = 'available';
			}

			// if more than one NAWS device, set flag to show device selection dropdown
			if (this.deviceList.length > 1) {
				this.showDeviceSelection = true;
			}

			if (
				this.activeDevice &&
				this.activeDevice.PrinterInfo.printer &&
				this.activeDevice.PrinterInfo.printer.length > 0
			) {
				printerNamesArray = this.deviceEngine.getPrinterNamesArray(
					this.activeDevice.PrinterInfo.printer
				);
			}

			// set status to 'notAvailable' if the plugin needs an upgrade
			if (this.activeDevice && this.activeDevice.isUpgradeRequired) {
				this.status = 'notAvailable';
				this.isNawsConnected = true;
			}
		}

		Logger.logToServer(
			{
				category: 'nawsv3_info',
				number_of_devices: this.deviceList.length,
				printer_list: printerNamesArray,
				connection_dropped: this.isNawsConnected && this.status !== 'available',
			},
			Logger.INFO
		);

		if (printCallback) {
			printCallback();
		}
	};

	/**
	 * Filter Printers list.
	 * @param {array} rawPrinters
	 */
	static filterPrinters = rawPrinters => {
		const filteredPrinters = [];
		let printerName;

		if (rawPrinters && Array.isArray(rawPrinters)) {
			for (let i = 0; i < rawPrinters.length; i++) {
				printerName = rawPrinters[i].name;

				if (!this.deviceEngine.printerExclude.test(printerName)) {
					filteredPrinters.push(rawPrinters[i]);
				}
			}
		}

		return filteredPrinters;
	};

	/**
	 * Return printer information
	 * @param {*} callback
	 */
	static getPrinterInfo = callback => {
		const printerInfo = this.activeDevice
			? this.activeDevice.PrinterInfo
			: null;
		callback(printerInfo);
	};

	static refreshPrinters = callback => {
		const isNaws = this.deviceEngine.className.indexOf('Naws') !== -1;
		const activeDeviceSessionId =
			this.activeDevice && this.activeDevice.SessionID
				? this.activeDevice.SessionID
				: null;
		const printerInfoTime =
			this.activeDevice && this.activeDevice.PrinterInfoTime
				? this.activeDevice.PrinterInfoTime
				: null;
		let checkSessionId = false,
			sessionId,
			filteredPrinters = [];

		this.deviceEngine.refreshPrinters(
			responseObject => {
				let printerInfoObj;

				if (isNaws && typeof responseObject.nawsSessionInfo === 'object') {
					printerInfoObj = responseObject.nawsSessionInfo.PrinterInfo;
					checkSessionId = true;
					sessionId = responseObject.nawsSessionInfo.SessionID;

					if (printerInfoObj && printerInfoObj.printer === undefined) {
						printerInfoObj.printer = [];
					}

					if (
						this.activeDevice.SessionID ===
						responseObject.nawsSessionInfo.SessionID
					) {
						filteredPrinters = this.filterPrinters(printerInfoObj.printer);
						this.activeDevice = responseObject.nawsSessionInfo;
						this.activeDevice.PrinterInfo.printer = filteredPrinters;
					} else {
						filteredPrinters = this.activeDevice.PrinterInfo.printer;
					}
				}
				this.updateDeviceListAndHasPrinters(isNaws, filteredPrinters);
				if (callback) {
					callback(filteredPrinters, checkSessionId, sessionId);
				}
			},
			activeDeviceSessionId,
			printerInfoTime
		);
	};

	/**
	 * Get Weight from NAWS API.
	 * @param {function} callback to be executed after getting weight from plugin
	 */
	static getWeight = callback => {
		const sessionId = this.activeDevice ? this.activeDevice.SessionID : '';
		const serverHost = this.activeDevice
			? this.activeDevice.SdcConnectServerHost
			: '';

		this.deviceEngine.getWeight(
			responseObject => {
				callback(responseObject);
			},
			sessionId,
			serverHost
		);
	};

	/**
	 * Calibrate scale value when weight is negative.
	 * @param {function} callback to be executed after scale reset from plugin
	 */
	static resetScale = callback => {
		const sessionId = this.activeDevice ? this.activeDevice.SessionID : '';

		this.deviceEngine.resetScale(responseObject => {
			Logger.logToServer(
				{
					category: 'reset_scale',
					error_code: responseObject.errorCode,
				},
				Logger.INFO
			);
			callback(responseObject);
		}, sessionId);
	};

	/**
	 * Provides static utility to manage error handling with
	 * a common format that could be passed to the callbacks.
	 * @param {enum} errorCode
	 * @return {object}
	 */
	static getDefaultObject = errorCode => ({
		errorCode: errorCode || ErrorCodes.SUCCESS,
		errorDescription: '',
		data: {},
	});

	/**
	 * Get Session Security Asset from Plugin. This is used for Purchase Postage.
	 * @param {function} callback to be executed after getting security asset info from plugin
	 * @return {object}
	 * 	status - returns '1006' if there is not an active session, otherwise, '0'
	 * 	data - returns the value of engine.getSecurityAsset() if there is an active session
	 */
	static getClientFingerprint = callback => {
		let securityAssetData, responseObject;

		if (
			this.activeDevice &&
			this.deviceEngine.className.indexOf('Naws') !== -1
		) {
			securityAssetData = {
				errorCode: '0',
				data: this.activeDevice.SecurityAsset,
			};

			callback(securityAssetData);
		} else {
			responseObject = this.getDefaultObject(
				ErrorCodes.plugin.PLUGIN_NOT_INSTALLED
			);

			// passes error response back to close the confirm window
			callback(responseObject);
		}
	};
}
